import { useState, useEffect } from "react";
import { createLink, deleteLink } from "../api/ItemLinkApi";
import { useNavigate } from "react-router-dom";
import LayoutAction from "../actions/LayoutAction";
import TaskAction from "../actions/TaskAction";
import { useDispatch, useSelector } from "react-redux";
import { navigateTo } from "../Navigator";
import { fromJS } from "immutable";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { useTranslation } from "react-i18next";
import IssuesSection from "./item-view/Right/IssuesSection";
import type { JiraIssue } from "./item-view/Right/IssuesSection";
import ContactsSection from "./item-view/Right/ContactsSection";
import type { ContactRow } from "./item-view/Right/ContactsSection";
import CompaniesSection from "./item-view/Right/CompaniesSection";
import type { CompanyRow } from "./item-view/Right/CompaniesSection";
import DealsSection from "./item-view/Right/DealsSection";
import type { DealRow } from "./item-view/Right/DealsSection";
import ItemLinkAction from "../actions/ItemLinkAction";
import { Plus, PlusIcon } from "lucide-react";
import { ActivityTabs } from "../activities/ItemActivityLogs";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import JiraIssueSelect from "../components/JiraIssueSelect";
import ItemSelect from "../components/ItemSelect";

interface ItemLink {
  id?: string;
  projectId: string;
  sourceObjectId: string;
  sourceObjectType: string;
  targetObjectId: string;
  targetObjectType: string;
  metadata: any;
}

type ItemLinkViewProps = {
  dataKey: string;
  projectId: string;
  objectId: string;
  objectType: string;
  itemLinks: any[];
  hasPrimaryContact?: boolean;
  primaryContactId?: string | null;
  hasPrimaryAccount?: boolean;
  primaryAccountId?: string | null;
};

const ItemLinkView = ({
  dataKey,
  projectId,
  objectId,
  objectType,
  itemLinks,
  hasPrimaryContact = false,
  primaryContactId = null,
  hasPrimaryAccount = false,
  primaryAccountId = null,
}: ItemLinkViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const members =
    useSelector((state: any) => state.projectReducer.getIn(["project", "members"])) || fromJS([]);
  const buckets = useSelector((state: any) => state.bucketReducer.get("SALE")) || fromJS([]);
  const currency = useSelector((state: any) => state.projectReducer.getIn(["project", "currency"]));

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const [showPrimaryConfirm, setShowPrimaryConfirm] = useState(false);
  const [pendingPrimaryItem, setPendingPrimaryItem] = useState<any>(null);
  const [primaryMode, setPrimaryMode] = useState<any>(null);

  const isPrimaryForType = (type: string, id: string) => {
    if (type === "CONTACT") return !!primaryContactId && primaryContactId === id;
    if (type === "COMPANY" || type === "ACCOUNT")
      return !!primaryAccountId && primaryAccountId === id;
    return false;
  };

  useEffect(() => {
    if (pendingPrimaryItem && primaryMode) {
      setTimeout(() => setShowPrimaryConfirm(true), 500);
    }
  }, [pendingPrimaryItem, primaryMode]);

  const handleSubmit = (targetObject: any) => {
    const newLink: ItemLink = {
      sourceObjectId: objectId,
      sourceObjectType: objectType,
      projectId: targetObject.get("projectId"),
      targetObjectId: targetObject.get("value"),
      targetObjectType:
        targetObject.get("type") === "COMPANY" ? "ACCOUNT" : targetObject.get("type"),
      metadata: {},
    };
    if (objectId !== targetObject.get("id")) {
      const isFirstContactLink = newLink.targetObjectType === "CONTACT" && !hasPrimaryContact;
      const isFirstAccountLink = newLink.targetObjectType === "ACCOUNT" && !hasPrimaryAccount;
      createLink(newLink).then(() => {
        dispatch(ItemLinkAction.getItemLink(objectId));
        dispatch(
          LayoutAction.setNotification({
            title: newLink.targetObjectType === "CONTACT" ? t("Link contact") : t("Link account"),
            type: "success",
            message:
              newLink.targetObjectType === "CONTACT"
                ? t("Link contact successfully")
                : t("Link account successfully"),
          })
        );
        if (isFirstContactLink || isFirstAccountLink) {
          setPendingPrimaryItem(targetObject);
          setPrimaryMode(isFirstContactLink ? "CONTACT" : "COMPANY");
        }
      });
    } else {
      dispatch(
        LayoutAction.setNotification({
          title: newLink.targetObjectType === "CONTACT" ? t("Add contact") : t("Link account"),
          type: "error",
          message: t("Cannot link same data"),
        })
      );
    }
  };

  const handleDeleteLink = (linkId: string) => {
    setShowConfirm({
      show: true,
      title: t("Unlink"),
      description: t("Are you sure you want to unlink this item?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "UNLINK_ITEM",
      data: linkId,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "UNLINK_ITEM") {
      deleteLink(showConfirm.data).then(() => {
        dispatch(ItemLinkAction.getItemLink(objectId));
        dispatch(
          LayoutAction.setNotification({
            title: t("Unlink"),
            type: "success",
            message: t("Unlink successfully!"),
          })
        );
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const setPrimaryContact = (contact: any) => {
    let contactItem: any = contact;
    if (contactItem?.toJS) contactItem = contactItem.toJS();
    if (contactItem && contactItem.value === undefined && contactItem.id) {
      contactItem = {
        value: contactItem.id,
        label: contactItem?.data?.["name"]?.value,
        projectId: contactItem.projectId,
        type: "CONTACT",
      };
    }
    if (!contactItem) return;
    dispatch(TaskAction.updateTask(objectId, "contact", contactItem, objectType));
    dispatch(
      LayoutAction.setNotification({
        title: "Primary contact",
        type: "success",
        message: "Primary contact updated.",
      })
    );
  };

  const setPrimaryCompany = (account: any) => {
    let accountItem: any = account;
    if (accountItem?.toJS) accountItem = accountItem.toJS();
    if (accountItem && accountItem.value === undefined && accountItem.id) {
      accountItem = {
        value: accountItem.id,
        label: accountItem?.data?.["name"]?.value,
        projectId: accountItem.projectId,
        type: "COMPANY",
      };
    }
    if (!accountItem) return;
    dispatch(TaskAction.updateTask(objectId, "account", accountItem, objectType));
    dispatch(
      LayoutAction.setNotification({
        title: "Primary account",
        type: "success",
        message: "Primary account updated.",
      })
    );
  };

  const getObjectData = (link: any) => {
    const {
      sourceObject = {},
      sourceObjectType = "",
      targetObject = {},
      targetObjectType = "",
    } = link;
    if (sourceObject?.id === objectId) {
      return {
        linkId: link.id,
        objectData: targetObject,
        objectType: targetObjectType,
      };
    }
    return {
      linkId: link.id,
      objectData: sourceObject,
      objectType: sourceObjectType,
    };
  };

  // Helper function to parse status category from status value
  const parseStatusCategory = (status: string | undefined): "TODO" | "IN_PROGRESS" | "DONE" => {
    if (!status) return "TODO";
    const statusLower = status.toLowerCase();
    if (
      statusLower.includes("done") ||
      statusLower.includes("completed") ||
      statusLower.includes("resolved")
    ) {
      return "DONE";
    }
    if (statusLower.includes("progress") || statusLower.includes("in progress")) {
      return "IN_PROGRESS";
    }
    return "TODO";
  };

  const renderIssuesList = () => {
    const issueLinks = itemLinks.filter((link) => getObjectData(link).objectType === "ISSUE");

    const issues: JiraIssue[] = issueLinks.map((link) => {
      const data = getObjectData(link);
      const issueData = fromJS(data.objectData.data);

      // Get assignee info - can be object with value/label or nested object
      const assigneeData = issueData.get("assignee");
      let assigneeName: string | undefined;
      let assigneeAvatar: string | undefined;

      if (assigneeData) {
        // Check if assignee is an object with label (from data sample)
        if (assigneeData.get("label")) {
          assigneeName = assigneeData.get("label");
        } else if (assigneeData.get("value")) {
          // If value is an object, try to get displayName
          const assigneeValue = fromJS(assigneeData.get("value"));
          if (assigneeValue && assigneeValue.get("displayName")) {
            assigneeName = assigneeValue.get("displayName");
          }
        }
      }

      // Get assignee avatar - can be from assigneeAvatarUrl.value or assignee.avatarUrls
      const assigneeAvatarUrl = issueData.getIn(["assigneeAvatarUrl", "value"]);
      if (assigneeAvatarUrl) {
        assigneeAvatar = assigneeAvatarUrl;
      } else if (assigneeData?.get("value")) {
        const assigneeValue = fromJS(assigneeData.get("value"));
        if (assigneeValue?.getIn(["avatarUrls", "48x48"])) {
          assigneeAvatar = assigneeValue.getIn(["avatarUrls", "48x48"]);
        }
      }

      const statusValue = issueData.getIn(["status", "value"]);

      return {
        id: data.linkId,
        issueKey: issueData.getIn(["issueKey", "value"]),
        summary: issueData.getIn(["name", "value"]),
        issueType: issueData.getIn(["issueType", "value"]) || "Task", // Default to Task if not available
        status: statusValue,
        statusCategory: parseStatusCategory(statusValue),
        assigneeName,
        assigneeAvatar,
      } as JiraIssue;
    });

    const handleOpen = (issueKey: string) => {
      navigateTo({ jiraUrlParams: { issueKey }, appUrl: ``, jiraView: "issue", navigate });
    };

    return (
      <IssuesSection
        title={t("JIRA Issues")}
        action={
          <div className="flex items-center justify-end">
            <JiraIssueSelect
              type="CUSTOM"
              value={undefined}
              onChange={(issue: any) => handleSubmit(fromJS(issue))}
              placeholder={t("Select issue")}
              popoverPanelWidth={400}
              targetCustom={
                <button
                  type="button"
                  className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-md shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05)] px-1.5 py-1.5 hover:bg-gray-50 transition-colors"
                >
                  <Plus className="w-3 h-3 text-gray-900" />
                  <span className="text-xs font-medium text-gray-900 leading-[16px] px-1">
                    {t("Link Issue")}
                  </span>
                </button>
              }
            />
          </div>
        }
        issues={issues}
        onOpen={handleOpen}
        onUnlink={(id) => handleDeleteLink(id as string)}
      />
    );
  };

  const renderContactsList = () => {
    const items = itemLinks.map(getObjectData).filter((data) => data.objectType === "CONTACT");

    const rows: ContactRow[] = items.map((data) => {
      const itemData = fromJS(data.objectData.data);
      return {
        id: data.linkId,
        name: itemData.getIn(["name", "value"]),
        avatarUrl: data.objectData.iconUrl,
        isPrimary: isPrimaryForType("CONTACT", data.objectData.id),
        companyName: itemData.getIn(["account", "value", "label"]),
        email: itemData.getIn(["email", "value"]),
      };
    });

    const handleOpen = (linkId: string | number) => {
      const link = itemLinks.find((l) => l.id === linkId);
      if (link) {
        const data = getObjectData(link);
        const appUrl = `/workspaces/${data.objectData.projectId}/items/${data.objectData.id}?type=${data.objectType}`;
        navigate(appUrl);
      }
    };

    const handleSetPrimary = (linkId: string | number) => {
      const link = itemLinks.find((l) => l.id === linkId);
      if (link) {
        const data = getObjectData(link);
        setPrimaryContact(data.objectData);
      }
    };

    let title = "";
    let addLabel = t("Add");
    if (dataKey === ActivityTabs.CONTACT.key) {
      title = t("Contacts");
      addLabel = t("Add Contact");
    } else if (dataKey === ActivityTabs.TEAM.key) {
      title = t("Teams");
      addLabel = t("Add Team");
    }

    return (
      <ContactsSection
        title={title}
        action={
          <div className="flex items-center justify-end">
            <ItemSelect
              projectId={projectId}
              contentType="CONTACT"
              type="CUSTOM"
              value={undefined}
              onChange={(contact: any) => handleSubmit(fromJS(contact))}
              placeholder={t("Select contact")}
              popoverPanelWidth={400}
              targetCustom={
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 text-xs font-medium text-gray-900"
                >
                  <PlusIcon className="w-4 h-4" />
                  {addLabel}
                </button>
              }
            />
          </div>
        }
        items={rows}
        onOpen={handleOpen}
        onSetPrimary={handleSetPrimary}
        onUnlink={(id) => handleDeleteLink(id as string)}
      />
    );
  };

  const renderCompaniesList = () => {
    const items = itemLinks
      .map(getObjectData)
      .filter((data) => data.objectType === "COMPANY" || data.objectType === "ACCOUNT");

    const rows: CompanyRow[] = items.map((data) => {
      const itemData = fromJS(data.objectData.data);
      return {
        id: data.linkId,
        name: itemData.getIn(["name", "value"]),
        avatarUrl: data.objectData.iconUrl,
        isPrimary: isPrimaryForType(data.objectType, data.objectData.id),
        location: [
          itemData.getIn(["street", "value"]),
          itemData.getIn(["city", "value", "label"]),
          itemData.getIn(["state", "value", "label"]),
          itemData.getIn(["country", "value", "label"]),
        ]
          .filter(Boolean)
          .join(", "),
      };
    });

    const handleOpen = (linkId: string | number) => {
      const link = itemLinks.find((l) => l.id === linkId);
      if (link) {
        const data = getObjectData(link);
        const appUrl = `/workspaces/${data.objectData.projectId}/items/${data.objectData.id}?type=COMPANY`;
        navigate(appUrl);
      }
    };

    const handleSetPrimary = (linkId: string | number) => {
      const link = itemLinks.find((l) => l.id === linkId);
      if (link) {
        const data = getObjectData(link);
        setPrimaryCompany(data.objectData);
      }
    };

    return (
      <CompaniesSection
        title={t("Accounts")}
        action={
          <div className="flex items-center justify-end">
            <ItemSelect
              projectId={projectId}
              contentType="COMPANY"
              type="CUSTOM"
              value={undefined}
              onChange={(account: any) => handleSubmit(fromJS(account))}
              placeholder={t("Select account")}
              popoverPanelWidth={400}
              targetCustom={
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 text-xs font-medium text-gray-900"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("Add Account")}
                </button>
              }
            />
          </div>
        }
        items={rows}
        onOpen={handleOpen}
        onSetPrimary={handleSetPrimary}
        onUnlink={(id) => handleDeleteLink(id as string)}
      />
    );
  };

  const renderSalesList = () => {
    const items = itemLinks.map(getObjectData).filter((data) => data.objectType === "SALE");

    const rows: DealRow[] = items.map((data) => {
      const itemData = fromJS(data.objectData.data);
      const assignees = itemData.getIn(["assignees", "value"])?.toJS() || [];
      return {
        id: data.linkId,
        name: itemData.getIn(["name", "value"]),
        avatarUrl: itemData.getIn(["avatar", "value"]),
        assignees: assignees.map((assignee: any) => {
          const member =
            members.find((item) => item.get("accountId") == assignee) ||
            fromJS({ accountId: assignee, displayName: assignee });
          return {
            name: member.get("displayName"),
            avatarUrl: member.get("avatarUrl"),
          };
        }),
        bucket: buckets.toJS().find((item) => item.id === itemData.getIn(["bucketId", "value"])),
        estimatedRevenue: itemData.getIn(["estimatedRevenue", "value"]),
        estimatedProbability: itemData.getIn(["estimatedProbability", "value"]),
        currency: currency,
      };
    });

    const handleOpen = (linkId: string | number) => {
      const link = itemLinks.find((l) => l.id === linkId);
      if (link) {
        const data = getObjectData(link);
        const appUrl = `/workspaces/${data.objectData.projectId}/items/${data.objectData.id}?type=SALE`;
        navigate(appUrl);
      }
    };

    return (
      <DealsSection
        title={t("Deals")}
        action={
          <div className="flex items-center justify-end">
            <ItemSelect
              projectId={projectId}
              contentType="SALE"
              type="CUSTOM"
              value={undefined}
              onChange={(sale: any) => handleSubmit(fromJS(sale))}
              placeholder={t("Select deal")}
              popoverPanelWidth={400}
              targetCustom={
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 rounded-md shadow-xs hover:bg-gray-50 text-xs font-medium text-gray-900"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t("Add Deal")}
                </button>
              }
            />
          </div>
        }
        items={rows}
        onOpen={handleOpen}
        onUnlink={(id) => handleDeleteLink(id as string)}
      />
    );
  };

  const renderList = () => {
    if (dataKey === ActivityTabs.ISSUE.key) return renderIssuesList();
    if (dataKey === ActivityTabs.CONTACT.key || dataKey === ActivityTabs.TEAM.key)
      return renderContactsList();
    if (dataKey === ActivityTabs.COMPANY.key) return renderCompaniesList();
    if (dataKey === ActivityTabs.DEAL.key) return renderSalesList();
  };

  return (
    <div className="w-full mx-auto">
      {renderList()}
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
      <ConfirmDialog
        visible={showPrimaryConfirm}
        title={t(primaryMode === "COMPANY" ? "Primary account" : "Primary contact")}
        description={t(
          primaryMode === "COMPANY"
            ? "Do you want to set this account as Primary account?"
            : "Do you want to set this contact as Primary contact?"
        )}
        submitBtnLabel={t("Set as Primary")}
        closeBtnLabel="Close"
        onClose={() => {
          setShowPrimaryConfirm(false);
          setPendingPrimaryItem(null);
          setPrimaryMode(null);
        }}
        onSubmit={() => {
          if (pendingPrimaryItem) {
            if (primaryMode === "COMPANY") setPrimaryCompany(pendingPrimaryItem);
            else if (primaryMode === "CONTACT") setPrimaryContact(pendingPrimaryItem);
          }
          setShowPrimaryConfirm(false);
          setPendingPrimaryItem(null);
          setPrimaryMode(null);
        }}
      />
    </div>
  );
};

export default ItemLinkView;
